import { useState } from 'react'
import { MultiFactorError, MultiFactorResolver } from 'firebase/auth'
import { Errors } from 'config'
import { useAuthContext } from 'shared/components/AuthContext'
import type { FirebaseAuth } from 'shared/services'
import { LoadingStatus } from 'shared/types'
import { getUserOAuthProvider, isPopupClosedError } from 'shared/utils'
import { useReauthenticationLink } from './use-reauthentication-link'

export enum ReauthenticationStep {
  FirstFactor = 'firstFactor',
  EmailLink = 'emailLink',
  TwoFactorCode = 'twoFactorCode',
}

// Reauthenticates the signed-in user with the first factor (password, provider or
// link), then with the 2FA code if the account has 2FA
export const useReauthentication = (
  onReauthenticated: () => Promise<void> | void
) => {
  const { auth, user } = useAuthContext()
  const [resolver, setResolver] = useState<MultiFactorResolver | null>(null)
  const [loading, setLoading] = useState(LoadingStatus.Idle)
  const [error, setError] = useState<string | null>(null)
  const oauthProvider = getUserOAuthProvider(user)

  const finish = async () => {
    setLoading(LoadingStatus.Succeeded)
    await onReauthenticated()
  }

  const link = useReauthenticationLink(finish)

  const fail = (code: string) => {
    setError(code)
    setLoading(LoadingStatus.Failed)
  }

  const runReauthentication = async (
    reauthenticate: (auth: FirebaseAuth) => Promise<unknown>
  ) => {
    // Without the user the calls resolve without reauthenticating
    if (!auth || !auth.getCurrentUser()) {
      fail(Errors.NullUser)
      return
    }

    setLoading(LoadingStatus.Pending)
    setError(null)

    try {
      await reauthenticate(auth)
    } catch (error: any) {
      const code = error?.code || Errors.InternalError

      if (code === Errors.MultiFactorAuthRequired) {
        setResolver(auth.getMultiFactorResolver(error as MultiFactorError))
        setLoading(LoadingStatus.Idle)
      } else if (isPopupClosedError(code)) {
        setLoading(LoadingStatus.Idle)
      } else {
        fail(code)
      }
      return
    }

    await finish()
  }

  const reauthenticateWithPassword = (password: string) =>
    runReauthentication(auth =>
      auth.reauthenticateWithPasswordCredential(
        auth.getCurrentUser()?.email ?? '',
        password
      )
    )

  const reauthenticateWithOAuthProvider = async () => {
    if (!oauthProvider) return
    await runReauthentication(auth =>
      auth.reauthenticateWithOAuthProvider(oauthProvider.providerId)
    )
  }

  const verifyCode = async (code: string) => {
    if (!resolver) return
    await runReauthentication(auth => auth.resolveTotpSignIn(resolver, code))
  }

  const sendLink = async () => {
    setError(null)
    const linkError = await link.send()
    if (linkError) setError(linkError)
  }

  // Back to the first factor. The 2FA resolver can't be reused.
  const goBack = () => {
    setResolver(null)
    link.cancel()
    setError(null)
    setLoading(LoadingStatus.Idle)
  }

  const getStep = () => {
    if (resolver) return ReauthenticationStep.TwoFactorCode
    if (link.isWaiting) return ReauthenticationStep.EmailLink
    return ReauthenticationStep.FirstFactor
  }

  return {
    step: getStep(),
    oauthProvider,
    loading,
    error,
    isLinkResent: link.isResent,
    isSendingLink: link.isSending,
    reauthenticateWithPassword,
    reauthenticateWithOAuthProvider,
    verifyCode,
    sendLink,
    goBack,
  }
}
